<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { createProjeto } from '@/api/projetos'
import { getApiErrorMessage } from '@/api/client'
import type { CampoMetadado } from '@/types/projetos'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const router = useRouter()
const saving = ref(false)

const nome = ref('')
const slug = ref('')
const ativo = ref(true)
const allowedOriginsText = ref('')
const retencaoDias = ref<number | ''>('')
const camposMetadados = ref<CampoMetadado[]>([])

const originsVazio = computed(() => allowedOriginsText.value.trim() === '')

function addCampo(): void {
  camposMetadados.value.push({ chave: '', rotulo: '', omitir: false })
}

function removeCampo(index: number): void {
  camposMetadados.value.splice(index, 1)
}

function parseOrigins(): string[] {
  return allowedOriginsText.value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
}

async function submit(): Promise<void> {
  saving.value = true
  try {
    const projeto = await createProjeto({
      nome: nome.value,
      slug: slug.value || undefined,
      ativo: ativo.value,
      allowed_origins: parseOrigins(),
      retencao_dias: retencaoDias.value === '' ? null : Number(retencaoDias.value),
      campos_metadados: camposMetadados.value.filter((c) => c.chave.trim() !== ''),
    })
    await router.push({
      path: `/admin/projetos/${projeto.id}`,
      state: { apiKey: projeto.api_key ?? '' },
    })
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Não foi possível criar',
      text: getApiErrorMessage(error, 'Erro ao criar projeto'),
    })
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <AdminLayout>
    <template #title>
      <h1 class="h5 mb-0">Novo projeto</h1>
    </template>

    <form class="card shadow-sm border-0 p-4" @submit.prevent="submit">
      <div class="mb-3">
        <label class="form-label" for="nome">Nome</label>
        <input id="nome" v-model="nome" type="text" class="form-control" required :disabled="saving">
      </div>

      <div class="mb-3">
        <label class="form-label" for="slug">Slug (opcional)</label>
        <input id="slug" v-model="slug" type="text" class="form-control" :disabled="saving">
      </div>

      <div class="form-check mb-3">
        <input id="ativo" v-model="ativo" class="form-check-input" type="checkbox" :disabled="saving">
        <label class="form-check-label" for="ativo">Projeto ativo</label>
      </div>

      <div class="mb-3">
        <label class="form-label" for="origins">Origens permitidas (uma por linha)</label>
        <div v-if="originsVazio" class="alert alert-warning small py-2 mb-2">
          Em produção, cadastre as origens do site. Lista vazia aceita qualquer origem (CORS).
        </div>
        <textarea
          id="origins"
          v-model="allowedOriginsText"
          class="form-control font-monospace"
          rows="3"
          placeholder="https://app.exemplo.com"
          :disabled="saving"
        />
      </div>

      <div class="mb-3">
        <label class="form-label" for="retencao">Retenção (dias, opcional)</label>
        <input
          id="retencao"
          v-model="retencaoDias"
          type="number"
          min="1"
          class="form-control"
          :disabled="saving"
        >
      </div>

      <div class="mb-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="form-label mb-0">Campos de metadados</label>
          <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="saving" @click="addCampo">
            Adicionar campo
          </button>
        </div>
        <div v-for="(campo, index) in camposMetadados" :key="index" class="row g-2 mb-2 align-items-center">
          <div class="col-md-4">
            <input v-model="campo.chave" type="text" class="form-control" placeholder="chave (ex: loja)" :disabled="saving">
          </div>
          <div class="col-md-4">
            <input v-model="campo.rotulo" type="text" class="form-control" placeholder="rótulo (ex: Loja)" :disabled="saving">
          </div>
          <div class="col-md-2">
            <div class="form-check">
              <input :id="`omitir-novo-${index}`" v-model="campo.omitir" class="form-check-input" type="checkbox" :disabled="saving">
              <label class="form-check-label small" :for="`omitir-novo-${index}`">Omitir</label>
            </div>
          </div>
          <div class="col-md-2">
            <button type="button" class="btn btn-outline-danger w-100" :disabled="saving" @click="removeCampo(index)">
              Remover
            </button>
          </div>
        </div>
      </div>

      <div class="d-flex gap-2">
        <button type="submit" class="btn btn-primary" :disabled="saving">Criar projeto</button>
        <RouterLink to="/admin/projetos" class="btn btn-outline-secondary">Cancelar</RouterLink>
      </div>
    </form>
  </AdminLayout>
</template>
